/* eslint-disable global-require */

import { SITE_NAME, TITLE_SUFFIX } from '../utils/seo';

/**
 * Описание по умолчанию — только для страниц, у которых своего ещё нет.
 * Задача — не оставить пустой meta description, а не подменить собой
 * нормальный текст: каждый значимый роут должен вернуть своё.
 */
const DEFAULT_DESCRIPTION =
	'New Tab Studio turns the Chrome new tab page into a dashboard you lay out yourself — countdowns, clocks, to-do lists, habit trackers, notes, photos and more.';

// The top-level (parent) route
const routes = {
	path: '',

	// Keep in mind, routes are evaluated in order
	children: [
		{
			path: '/',
			load: () => import(/* webpackChunkName: 'themes' */ './home'),
		},
		{
			path: '/themes',
			load: () => import(/* webpackChunkName: 'themes' */ './themes'),
		},
		{
			path: '/best-new-tab-chrome-extensions',
			load: () =>
				import(
					/* webpackChunkName: 'roundup' */ './best-new-tab-extensions'
				),
		},
		{
			path: '/how-to-change-chrome-background',
			load: () =>
				import(
					/* webpackChunkName: 'chrome-background' */ './how-to-change-chrome-background'
				),
		},
		{
			path: '/how-to-change-new-tab',
			load: () =>
				import(
					/* webpackChunkName: 'how-to' */ './how-to-change-new-tab'
				),
		},
		{
			path: '/widgets',
			load: () => import(/* webpackChunkName: 'widgets' */ './widgets'),
		},
		{
			path: '/widgets/:slug',
			load: () =>
				import(
					/* webpackChunkName: 'widget-page' */ './widgets/widget'
				),
		},
		{
			path: '/login',
			load: () => import(/* webpackChunkName: 'login' */ './login'),
		},
		{
			path: '/register',
			load: () => import(/* webpackChunkName: 'register' */ './register'),
		},
		{
			path: '/about',
			load: () => import(/* webpackChunkName: 'about' */ './about'),
		},
		{
			path: '/invite',
			load: () => import(/* webpackChunkName: 'invite' */ './invite'),
		},
		{
			// Только авторизованные. @see src/router.js
			path: '/invited',
			load: () => import(/* webpackChunkName: 'invited' */ './invited'),
		},
		{
			path: '/join/:userId',
			load: () => import(/* webpackChunkName: 'join' */ './join'),
		},
		{
			path: '/joined',
			load: () => import(/* webpackChunkName: 'joined' */ './joined'),
		},
		{
			path: '/close',
			load: () => import(/* webpackChunkName: 'close' */ './close'),
		},
		{
			path: '/theme/:id',
			load: () => import(/* webpackChunkName: 'theme' */ './theme'),
		},
		{
			path: '/theme/edit/:id',
			load: () =>
				import(/* webpackChunkName: 'create-theme' */ './theme/create'),
		},
		{
			path: '/themes/by/:userId',
			load: () =>
				import(/* webpackChunkName: 'by-themes' */ './themes/by'),
		},
		{
			path: '/themes/my',
			load: () =>
				import(/* webpackChunkName: 'my-themes' */ './themes/my'),
		},
		{
			path: '/themes/all',
			load: () =>
				import(/* webpackChunkName: 'all-themes' */ './themes/all'),
		},
		{
			path: '/gallery/:guid',
			load: () => import(/* webpackChunkName: 'gallery' */ './gallery'),
		},
		{
			path: '/top/:query',
			load: () => import(/* webpackChunkName: 'top' */ './top'),
		},
		{
			path: '/top',
			load: () => import(/* webpackChunkName: 'top' */ './top'),
		},
		{
			path: '/pro',
			load: () => import(/* webpackChunkName: 'pro' */ './pro'),
		},
		{
			path: '/500',
			load: () => import(/* webpackChunkName: 'error500' */ './error500'),
		},
		{
			path: '/pro/dev-test',
			load: () => import(/* webpackChunkName: 'pro-dev' */ './pro/dev'),
		},
		{
			path: '/support',
			load: () => import(/* webpackChunkName: 'support' */ './support'),
		},
		{
			path: '/wallpaper/:id',
			load: () =>
				import(/* webpackChunkName: 'wallpaper' */ './wallpaper'),
		},
		{
			// Только для админа, остальным — 404. @see ./admin/users
			path: '/admin/users',
			load: () =>
				import(/* webpackChunkName: 'admin-users' */ './admin/users'),
		},
		// Wildcard routes, e.g. { path: '(.*)', ... } (must go last)
		{
			path: '(.*)',
			load: () =>
				import(/* webpackChunkName: 'not-found' */ './not-found'),
		},
	],

	async action({ next }) {
		// Execute each child route until one of them return the result
		const route = await next();

		// Заголовок: суффикс с брендом добавляем, если роут его ещё не содержит
		// и явно не отказался от него через titleFull.
		const title = route.title || 'Untitled Page';
		route.title = route.titleFull
			? title
			: `${title}${title.includes(SITE_NAME) ? '' : TITLE_SUFFIX}`;

		// Описание обязательно: пустой description на каждой странице —
		// то, с чего начинался SEO-аудит этого сайта. @see docs/seo/SEO-AUDIT.md
		route.description = route.description || DEFAULT_DESCRIPTION;

		return route;
	},
};

// The error page is available by permanent url for development mode
if (__DEV__) {
	routes.children.unshift({
		path: '/error',
		action: require('./error').default,
	});
}

export default routes;
